// A14 — Email broadcast. TVP2 §9 styling: bordered Paper card, MUI
// standard inputs, Bootstrap-blue Send / grey Discard. Content is a
// real WYSIWYG (Tiptap; see components/WysiwygEditor) so the operator
// can format without typing raw HTML.
//
// Server still sanitises with `xss` before persisting / sending — any
// extension we later add to the editor remains bounded by the server
// allowlist.

import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Box,
  Button,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { tokens } from "@findgeeks/ui-tokens";
import { ApiError, apiFetch } from "@/lib/api";
import { ConfirmModal } from "@/components/ConfirmModal";
import { useToast } from "@/components/Toast";
import { WysiwygEditor } from "@/components/WysiwygEditor";

const USER_GROUPS = [
  "Primary",
  "Secondary",
  "All Engineers",
  "All Customers",
  "All",
];

interface RecipientCountResp {
  count: number;
}

interface BroadcastResp {
  ok: boolean;
  recipient_count: number;
  status: string;
}

// Must match the production `RESEND_FROM_EMAIL` in apps/api/wrangler.toml.
// If you change one, change the other — admins shouldn't see a different
// sender on this form than what actually goes out.
const FIXED_FROM = "FindGeeks <noreply@findgeeks.profexint.com>";

function friendlyError(err: unknown, fallback: string): string {
  if (!(err instanceof ApiError)) {
    return err instanceof Error ? err.message : fallback;
  }
  const code =
    (err.body && typeof err.body === "object" && "error" in err.body
      ? String((err.body as { error?: unknown }).error ?? "")
      : "") || err.message;
  switch (code) {
    case "broadcast_cap_exceeded_phase1_10k":
      return "Recipient list exceeds the 10,000 cap. Split into smaller user-group segments.";
    case "too_many_requests":
      return "Daily broadcast quota reached. Try again tomorrow.";
    case "bad_request":
      return "Invalid input. Double-check the form and try again.";
    default:
      return err.message || fallback;
  }
}

// Tiptap renders an empty editor as '<p></p>'. Treat that as 'no content'
// for the Send-disabled gate so the operator can't queue a blank email.
const isEmptyHtml = (html: string): boolean =>
  html.replace(/<[^>]+>/g, "").trim().length === 0;

export function EmailNotifications() {
  const { show, ToastEl } = useToast();
  const [userGroup, setUserGroup] = useState("Primary");
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const recipientCount = useQuery<RecipientCountResp>({
    queryKey: ["admin", "recipient-count", userGroup],
    queryFn: () =>
      apiFetch<RecipientCountResp>(
        `/v1/admin/notifications/recipient-count?user_group=${encodeURIComponent(userGroup)}`,
      ),
    enabled: confirmOpen,
  });

  const send = useMutation({
    mutationFn: () =>
      apiFetch<BroadcastResp>("/v1/admin/notifications/email", {
        method: "POST",
        body: { user_group: userGroup, subject, html },
      }),
    onSuccess: (res) => {
      show(
        `Broadcast queued — delivery in progress (${res.recipient_count} recipients).`,
        "success",
      );
      setSubject("");
      setHtml("");
      setConfirmOpen(false);
    },
    onError: (err) => {
      show(friendlyError(err, "Failed to queue broadcast"), "error");
      setConfirmOpen(false);
    },
  });

  const sendDisabled =
    !userGroup || !subject.trim() || isEmptyHtml(html) || send.isPending;

  const cardSx = {
    border: `1px solid ${tokens.colors.admin.border}`,
    borderRadius: 1,
    p: 3,
  };
  const sectionHeading = {
    fontSize: tokens.font.sizes.body,
    fontWeight: tokens.font.weights.semibold,
    color: tokens.colors.admin.textHeading,
    mb: 3,
  } as const;
  const baseBtn = {
    px: 4,
    textTransform: "none" as const,
    fontWeight: tokens.font.weights.medium,
    color: "#fff",
    boxShadow: "none",
  };
  const primaryBtn = {
    ...baseBtn,
    backgroundColor: "#007bff",
    "&:hover": { backgroundColor: "#0069d9", boxShadow: "none" },
    "&.Mui-disabled": {
      backgroundColor: "#7BB1F0",
      color: "rgba(255,255,255,0.85)",
    },
  };
  const greyBtn = {
    ...baseBtn,
    backgroundColor: "#D8D8D8",
    "&:hover": { backgroundColor: "#C8C8C8", boxShadow: "none" },
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper elevation={0} sx={cardSx}>
        <Typography sx={sectionHeading}>Email Notifications</Typography>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr 1fr" },
            gap: 3,
            mb: 3,
          }}
        >
          <Box sx={{ gridColumn: { xs: "auto", md: "span 2" } }}>
            <TextField
              variant="standard"
              fullWidth
              label="From"
              value={FIXED_FROM}
              disabled
            />
          </Box>
          <Box sx={{ gridColumn: { xs: "auto", md: "span 2" } }}>
            <TextField
              variant="standard"
              fullWidth
              required
              select
              label="Choose User Group"
              value={userGroup}
              onChange={(e) => setUserGroup(e.target.value)}
            >
              {USER_GROUPS.map((g) => (
                <MenuItem key={g} value={g}>
                  {g}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </Box>
        <Box sx={{ mb: 3 }}>
          <TextField
            variant="standard"
            fullWidth
            required
            label="Subject"
            placeholder="Type here..."
            inputProps={{ maxLength: 200 }}
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            helperText={`${subject.length}/200`}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{
              fontSize: tokens.font.sizes.label,
              color: tokens.colors.admin.textMuted,
              mb: 0.5,
            }}
          >
            Content *
          </Typography>
          <WysiwygEditor
            value={html}
            onChange={setHtml}
            minRows={10}
            placeholder="Write your message…"
          />
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            disabled={sendDisabled}
            sx={primaryBtn}
            onClick={() => setConfirmOpen(true)}
          >
            Send
          </Button>
          <Button
            variant="contained"
            sx={greyBtn}
            onClick={() => {
              setSubject("");
              setHtml("");
            }}
          >
            Discard
          </Button>
        </Stack>
      </Paper>

      <ConfirmModal
        open={confirmOpen}
        title="Confirm email broadcast"
        body={
          <>
            <Typography>
              About to send an email to{" "}
              <strong>
                {recipientCount.isLoading
                  ? "…"
                  : recipientCount.data?.count ?? "?"}
              </strong>{" "}
              <strong>{userGroup}</strong> recipients. This action cannot be
              undone. Continue?
            </Typography>
            {(recipientCount.data?.count ?? 0) > 10000 && (
              <Typography
                sx={{ mt: 1, color: "error.main", fontWeight: 600 }}
              >
                ⚠ Recipient list exceeds the Phase-1 cap of 10,000. Send
                will return broadcast_cap_exceeded_phase1_10k — split this
                into smaller user-group segments first.
              </Typography>
            )}
          </>
        }
        confirmLabel="Send"
        pending={send.isPending}
        onConfirm={() => send.mutate()}
        onCancel={() => setConfirmOpen(false)}
      />
      {ToastEl}
    </Box>
  );
}
